import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { photoUrl } from "@/lib/api";
import {
  Plus,
  Trash2,
  Search,
  FileText,
  ExternalLink,
  Image as ImageIcon,
  X,
  Loader2,
  Check,
  ChevronDown,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

export default function ProductTable({ productType, onAfterChange, highlightProductId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [photoModal, setPhotoModal] = useState(null); // { src }
  const [photoEditor, setPhotoEditor] = useState(null); // product object
  const [savingId, setSavingId] = useState(null);
  // Row that should be scrolled into view + briefly pulsed when the user
  // picked it from the manufacturer-wide product search.
  const rowRefs = useRef({});

  useEffect(() => {
    if (!highlightProductId) return;
    // Wait a tick so the row has rendered (especially right after a tab switch)
    const t = setTimeout(() => {
      const el = rowRefs.current[highlightProductId];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 120);
    return () => clearTimeout(t);
  }, [highlightProductId, products]);

  const columns = productType.columns || [];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/product-types/${productType.id}/products`);
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType.id]);

  const filtered = useMemo(() => {
    if (!query.trim()) return products;
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (p.name?.toLowerCase().includes(q)) return true;
      if (p.model_number?.toLowerCase().includes(q)) return true;
      for (const col of columns) {
        const v = p.attributes?.[col.key];
        if (v != null && String(v).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [products, query, columns]);

  const onAddRow = async () => {
    setAdding(true);
    try {
      const { data } = await api.post(`/product-types/${productType.id}/products`, {
        name: "Untitled product",
        model_number: "",
        spec_sheet_url: "",
        photo_ids: [],
        attributes: {},
      });
      setProducts((prev) => [...prev, data]);
      onAfterChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const saveProduct = async (productId, patch) => {
    setSavingId(productId);
    try {
      const { data } = await api.patch(`/products/${productId}`, patch);
      setProducts((prev) => prev.map((p) => (p.id === productId ? data : p)));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const onDeleteRow = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/products/${confirmDelete.id}`);
      setProducts((prev) => prev.filter((p) => p.id !== confirmDelete.id));
      setConfirmDelete(null);
      onAfterChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="relative w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search products…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 rounded-sm h-9"
            data-testid="product-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">
            {filtered.length} of {products.length}
          </div>
          <Button
            onClick={onAddRow}
            disabled={adding}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-sm"
            data-testid="add-product-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Add product
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="byrd-progress" />
      ) : products.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-10 text-center bg-slate-50">
          <div className="font-display text-base font-semibold text-slate-900">
            No products yet
          </div>
          <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
            Click "Add product" to insert a new row. Each row gets all the
            columns you defined for this product type.
          </p>
          <Button
            onClick={onAddRow}
            className="mt-4 bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="empty-add-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add product
          </Button>
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-auto max-h-[640px] bg-white">
          <table className="byrd-table">
            <thead>
              <tr>
                <th className="sticky left-0 bg-slate-100 z-10 min-w-[200px]">Name</th>
                <th className="min-w-[160px]">Type</th>
                <th className="min-w-[180px]">Aka</th>
                <th className="min-w-[180px]">Description</th>
                <th className="min-w-[140px]">Model #</th>
                <th className="min-w-[120px]">Photos</th>
                <th className="min-w-[160px]">Spec sheet</th>
                {columns.map((c) => (
                  <th key={c.id} className="min-w-[140px]">
                    {c.label}
                    {c.required && <span className="text-amber-600 ml-1">*</span>}
                  </th>
                ))}
                <th className="w-12" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  ref={(el) => { rowRefs.current[p.id] = el; }}
                  data-testid={`product-row-${p.id}`}
                  className={
                    highlightProductId === p.id
                      ? "ring-2 ring-amber-400 ring-offset-1 bg-amber-50 transition-colors"
                      : ""
                  }
                >
                  <td className="editable-cell sticky left-0 bg-white z-[1]">
                    <InlineText
                      value={p.name}
                      onSave={(v) => saveProduct(p.id, { name: v })}
                      placeholder="Product name"
                    />
                  </td>
                  <td className="editable-cell">
                    <InlineText
                      value={p.type || ""}
                      onSave={(v) => saveProduct(p.id, { type: v })}
                      placeholder="e.g. Area Light"
                    />
                  </td>
                  <td className="editable-cell">
                    <InlineText
                      value={(p.aka || []).join(", ")}
                      onSave={(v) =>
                        saveProduct(p.id, {
                          aka: v
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean),
                        })
                      }
                      placeholder="parking lot light, pole light"
                    />
                  </td>
                  <td className="editable-cell">
                    <InlineText
                      value={p.description || ""}
                      onSave={(v) => saveProduct(p.id, { description: v })}
                      placeholder="e.g. Most economical"
                    />
                  </td>
                  <td className="editable-cell">
                    <InlineText
                      value={p.model_number}
                      onSave={(v) => saveProduct(p.id, { model_number: v })}
                      placeholder="Model #"
                      mono
                    />
                  </td>
                  <td>
                    <PhotosCell
                      product={p}
                      onOpenEditor={() => setPhotoEditor(p)}
                      onOpenPhoto={(src) => setPhotoModal({ src })}
                      onUploaded={() => {
                        load();
                        onAfterChange?.();
                      }}
                    />
                  </td>
                  <td>
                    <SpecSheetCell
                      value={p.spec_sheet_url}
                      onSave={(v) => saveProduct(p.id, { spec_sheet_url: v })}
                    />
                  </td>
                  {columns.map((c) => (
                    <td key={c.id} className="editable-cell">
                      <AttributeCell
                        column={c}
                        value={p.attributes?.[c.key]}
                        onSave={(v) =>
                          saveProduct(p.id, {
                            attributes: { ...(p.attributes || {}), [c.key]: v },
                          })
                        }
                      />
                    </td>
                  ))}
                  <td className="text-center">
                    {savingId === p.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground inline" />
                    ) : (
                      <button
                        onClick={() => setConfirmDelete(p)}
                        className="text-muted-foreground hover:text-destructive p-1"
                        data-testid={`delete-product-${p.id}`}
                        aria-label="Delete product"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this product?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{confirmDelete?.name}". This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteRow}
              className="bg-destructive hover:bg-red-600 text-white rounded-sm"
              data-testid="confirm-delete-product"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!photoModal} onOpenChange={(o) => !o && setPhotoModal(null)}>
        <DialogContent className="rounded-sm max-w-3xl bg-slate-900 border-slate-800 p-2">
          <DialogHeader className="sr-only">
            <DialogTitle>Photo</DialogTitle>
          </DialogHeader>
          {photoModal?.src && (
            <img src={photoModal.src} alt="Preview" className="w-full h-auto rounded-sm" />
          )}
        </DialogContent>
      </Dialog>

      <PhotoEditorDialog
        product={photoEditor}
        allProducts={products}
        productTypeName={productType?.name || ""}
        onClose={() => setPhotoEditor(null)}
        onSaved={(updated) => {
          setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
          setPhotoEditor(null);
        }}
        onApplied={() => {
          // After bulk-apply, reload the whole table since many rows changed
          load();
          onAfterChange?.();
        }}
      />
    </div>
  );
}

function InlineText({ value, onSave, placeholder, mono }) {
  const [v, setV] = useState(value || "");
  useEffect(() => {
    setV(value || "");
  }, [value]);
  return (
    <input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        if ((value || "") !== v) onSave(v);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setV(value || "");
          e.currentTarget.blur();
        }
      }}
      placeholder={placeholder}
      className={mono ? "font-mono-data" : ""}
    />
  );
}

function AttributeCell({ column, value, onSave }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => {
    setV(value ?? "");
  }, [value]);

  const commit = (newVal) => {
    if ((value ?? "") !== newVal) onSave(newVal);
  };

  if (column.type === "boolean") {
    const isYes = value === true || value === "true";
    return (
      <button
        onClick={() => onSave(!isYes)}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-medium border ${
          isYes
            ? "bg-amber-50 border-amber-300 text-amber-700"
            : "bg-slate-50 border-border text-muted-foreground"
        }`}
      >
        {isYes ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
        {isYes ? "Yes" : "No"}
      </button>
    );
  }

  if (column.type === "option") {
    const current = ["Standard", "Optional", "Not Available"].includes(value) ? value : "Optional";
    const palette = {
      Standard: "bg-emerald-50 border-emerald-300 text-emerald-700",
      Optional: "bg-amber-50 border-amber-300 text-amber-700",
      "Not Available": "bg-slate-100 border-border text-muted-foreground",
    };
    return (
      <select
        value={current}
        onChange={(e) => onSave(e.target.value)}
        className={`px-2 py-0.5 rounded-sm text-xs font-medium border ${palette[current]}`}
      >
        <option value="Standard">Standard</option>
        <option value="Optional">Optional</option>
        <option value="Not Available">Not Available</option>
      </select>
    );
  }

  if (column.type === "multiselect") {
    const selected = Array.isArray(value) ? value : value ? [String(value)] : [];
    const opts = column.options || [];
    const toggle = (opt) => {
      const next = selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt];
      onSave(next);
    };
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="w-full text-left px-1 py-1 inline-flex items-center gap-1 flex-wrap min-h-[26px] hover:bg-slate-50 rounded-sm"
            data-testid={`multiselect-cell-${column.key}`}
          >
            {selected.length === 0 ? (
              <span className="text-muted-foreground text-xs italic px-1">— pick —</span>
            ) : (
              selected.map((v) => (
                <span
                  key={v}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-medium"
                >
                  {v}
                </span>
              ))
            )}
            <ChevronDown className="w-3 h-3 ml-auto text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2 rounded-sm" align="start">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data px-1 pb-1">
            Offered values
          </div>
          <div className="max-h-60 overflow-y-auto">
            {opts.length === 0 ? (
              <div className="text-xs text-muted-foreground px-1 py-2">
                No options defined yet — edit the column to add some.
              </div>
            ) : (
              opts.map((o) => {
                const checked = selected.includes(o);
                return (
                  <label
                    key={o}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-slate-50 cursor-pointer text-sm"
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggle(o)} />
                    <span className={checked ? "font-medium text-slate-900" : "text-slate-700"}>{o}</span>
                  </label>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  if (column.type === "dropdown") {
    return (
      <select
        value={v ?? ""}
        onChange={(e) => {
          setV(e.target.value);
          commit(e.target.value);
        }}
      >
        <option value=""></option>
        {(column.options || []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      type={column.type === "number" ? "number" : column.type === "url" ? "url" : "text"}
      value={v ?? ""}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => commit(v)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setV(value ?? "");
          e.currentTarget.blur();
        }
      }}
      className={column.type === "number" ? "font-mono-data" : ""}
    />
  );
}

function SpecSheetCell({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);

  if (!editing && value) {
    return (
      <div className="flex items-center justify-between gap-2 px-2">
        <a
          href={value.startsWith("http") ? value : `https://${value}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900 text-xs"
        >
          <FileText className="w-3.5 h-3.5 text-amber-600" />
          Open PDF
          <ExternalLink className="w-3 h-3" />
        </a>
        <button
          onClick={() => setEditing(true)}
          className="text-[11px] text-muted-foreground hover:text-slate-900 uppercase tracking-wider font-mono-data"
        >
          edit
        </button>
      </div>
    );
  }

  return (
    <div className="editable-cell">
      <input
        autoFocus={editing}
        value={v}
        placeholder="https://…/spec.pdf"
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          if ((value || "") !== v) onSave(v);
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setV(value || "");
            setEditing(false);
          }
        }}
      />
    </div>
  );
}

function PhotosCell({ product, onOpenEditor, onOpenPhoto, onUploaded }) {
  const photos = product.photo_ids || [];
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter((f) =>
      f.type && f.type.startsWith("image/")
    );
    if (list.length === 0) {
      toast.error("Only image files can be dropped here");
      return;
    }
    setUploading(true);
    try {
      const newIds = [];
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newIds.push(data.id);
      }
      const next = [...photos, ...newIds];
      const { data } = await api.patch(`/products/${product.id}`, {
        photo_ids: next,
      });
      onUploaded?.(data);
      toast.success(
        `Uploaded ${newIds.length} photo${newIds.length > 1 ? "s" : ""}`
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  return (
    <div
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`flex items-center gap-1.5 flex-wrap rounded-sm p-1 -m-1 transition-colors ${
        dragOver
          ? "ring-2 ring-amber-500 bg-amber-50"
          : ""
      }`}
      data-testid={`photos-cell-${product.id}`}
    >
      {photos.slice(0, 3).map((pid) => (
        <button
          key={pid}
          onClick={() => onOpenPhoto(photoUrl(pid))}
          className="w-9 h-9 border border-border rounded-sm overflow-hidden hover:border-slate-900"
        >
          <img src={photoUrl(pid)} alt="" className="w-full h-full object-cover" />
        </button>
      ))}
      {photos.length > 3 && (
        <span className="text-[11px] text-muted-foreground font-mono-data">
          +{photos.length - 3}
        </span>
      )}
      <button
        onClick={onOpenEditor}
        className="w-9 h-9 border border-dashed border-border rounded-sm flex items-center justify-center text-muted-foreground hover:text-slate-900 hover:border-slate-900"
        data-testid={`edit-photos-${product.id}`}
        aria-label="Edit photos or drag-drop to add"
        title="Click to edit photos · drag and drop image files here to add"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ImageIcon className="w-4 h-4" />
        )}
      </button>
      {dragOver && (
        <span className="text-[10px] uppercase tracking-wider font-mono-data text-amber-700 ml-1">
          Drop to upload
        </span>
      )}
    </div>
  );
}

function PhotoEditorDialog({
  product,
  allProducts = [],
  productTypeName = "",
  onClose,
  onSaved,
  onApplied,
}) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // "Apply to..." sub-dialog state
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyMode, setApplyMode] = useState("empty"); // 'empty' | 'all'
  const [applyBusy, setApplyBusy] = useState(false);

  if (!product) return null;
  const photos = product.photo_ids || [];

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const newIds = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newIds.push(data.id);
      }
      const next = [...photos, ...newIds];
      const { data } = await api.patch(`/products/${product.id}`, { photo_ids: next });
      onSaved(data);
      toast.success(`Uploaded ${newIds.length} photo${newIds.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (pid) => {
    try {
      const next = photos.filter((x) => x !== pid);
      const { data } = await api.patch(`/products/${product.id}`, { photo_ids: next });
      onSaved(data);
      api.delete(`/photos/${pid}`).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Compute eligible target rows for the "Apply to..." action.
  // Excludes the source row itself. "empty" mode only targets rows that
  // currently have zero photos.
  const otherProducts = allProducts.filter((p) => p.id !== product.id);
  const targetsAll = otherProducts;
  const targetsEmpty = otherProducts.filter(
    (p) => !p.photo_ids || p.photo_ids.length === 0
  );
  const targets = applyMode === "empty" ? targetsEmpty : targetsAll;

  const applyToTargets = async () => {
    if (photos.length === 0) {
      toast.error("Add at least one photo first");
      return;
    }
    if (targets.length === 0) {
      toast.info(
        applyMode === "empty"
          ? "All other rows already have photos — nothing to apply to."
          : "No other rows in this product type."
      );
      return;
    }
    setApplyBusy(true);
    try {
      // Patch each target in parallel (small batches to be polite to the API)
      const batch = 5;
      for (let i = 0; i < targets.length; i += batch) {
        const slice = targets.slice(i, i + batch);
        await Promise.all(
          slice.map((p) =>
            api.patch(`/products/${p.id}`, { photo_ids: [...photos] })
          )
        );
      }
      toast.success(
        `Applied ${photos.length} photo${photos.length === 1 ? "" : "s"} to ${targets.length} row${targets.length === 1 ? "" : "s"}`
      );
      setApplyOpen(false);
      onApplied?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err, "Could not apply to all rows"));
    } finally {
      setApplyBusy(false);
    }
  };

  return (
    <>
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Photos · {product.name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {photos.map((pid) => (
            <div key={pid} className="relative group">
              <img
                src={photoUrl(pid)}
                alt=""
                className="w-full aspect-square object-cover border border-border rounded-sm"
              />
              <button
                onClick={() => removePhoto(pid)}
                className="absolute top-1 right-1 bg-slate-900/80 text-white rounded-sm p-1 opacity-0 group-hover:opacity-100"
                aria-label="Remove photo"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <label className="aspect-square border border-dashed border-border rounded-sm flex flex-col items-center justify-center text-muted-foreground hover:text-slate-900 hover:border-slate-900 cursor-pointer">
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Plus className="w-5 h-5" />
                <span className="text-[10px] uppercase mt-1 tracking-wider">Add</span>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={onPick}
              data-testid="photo-upload-input"
            />
          </label>
        </div>
        <div className="text-[11px] text-muted-foreground">
          PNG, JPG, WEBP up to 5MB each.
        </div>

        {/* Apply-to-all action — only shown when this row has at least one
            photo AND there are other rows in the product type. */}
        {photos.length > 0 && otherProducts.length > 0 && (
          <div className="border-t border-border pt-3 mt-1">
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm w-full justify-start"
              onClick={() => setApplyOpen(true)}
              data-testid="apply-photos-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Apply these photo{photos.length === 1 ? "" : "s"} to other rows...
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Confirmation dialog for bulk apply */}
    <AlertDialog open={applyOpen} onOpenChange={setApplyOpen}>
      <AlertDialogContent className="rounded-sm" data-testid="apply-photos-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">
            Apply photo{photos.length === 1 ? "" : "s"} to other rows
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2 pt-2">
            <span className="block">
              Source row:{" "}
              <span className="font-mono-data text-slate-900">{product.name}</span>
              {productTypeName && (
                <>
                  {" "}
                  in <span className="font-mono-data">{productTypeName}</span>
                </>
              )}
            </span>
            <span className="block">
              {photos.length} photo{photos.length === 1 ? "" : "s"} will be copied
              to the selected target rows. This{" "}
              <span className="font-semibold">replaces</span> the existing photo
              set on each target row.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 py-2">
          <label className="flex items-start gap-3 p-3 border border-border rounded-sm hover:bg-slate-50 cursor-pointer">
            <input
              type="radio"
              name="apply-mode"
              value="empty"
              checked={applyMode === "empty"}
              onChange={() => setApplyMode("empty")}
              className="mt-0.5"
              data-testid="apply-mode-empty"
            />
            <div className="flex-1">
              <div className="text-sm font-medium">
                Only rows without photos
              </div>
              <div className="text-xs text-muted-foreground">
                Safer. Will affect{" "}
                <span className="font-mono-data text-slate-900">
                  {targetsEmpty.length}
                </span>{" "}
                row{targetsEmpty.length === 1 ? "" : "s"}.
              </div>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 border border-border rounded-sm hover:bg-slate-50 cursor-pointer">
            <input
              type="radio"
              name="apply-mode"
              value="all"
              checked={applyMode === "all"}
              onChange={() => setApplyMode("all")}
              className="mt-0.5"
              data-testid="apply-mode-all"
            />
            <div className="flex-1">
              <div className="text-sm font-medium">
                All other rows in this product type
              </div>
              <div className="text-xs text-muted-foreground">
                Overwrites existing photos on{" "}
                <span className="font-mono-data text-slate-900">
                  {targetsAll.length}
                </span>{" "}
                row{targetsAll.length === 1 ? "" : "s"}.
              </div>
            </div>
          </label>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={applyBusy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              applyToTargets();
            }}
            disabled={applyBusy || targets.length === 0}
            className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
            data-testid="apply-photos-confirm"
          >
            {applyBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Apply to {targets.length} row{targets.length === 1 ? "" : "s"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
